import { SvgIcon } from '@mui/material';
import { SearchkitClient, useSearchkit } from '@searchkit/client';
import { isEmpty, isEqual } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { search } from 'sr-types';
import ResponsiveButtonGroup, { ResponsiveButtonGroupOption } from '../../../common/components/ResponsiveButtonGroup';
import { TYPE_TO_ICON } from '../../../common/icons/typeToIcon';

const getFilter = (api: SearchkitClient, identifier: string) => {
    const filters = api.getFiltersByIdentifier(identifier).map((filter) => filter.value);
    return !isEmpty(filters) ? filters[0] : '';
};

const doFilter = (api: SearchkitClient, identifier: string, newValue: string) => {
    const apiFilter = getFilter(api, identifier);
    if (!isEqual(apiFilter, newValue)) {
        const pageSize = api.getSearchState().page.size;
        api.removeFiltersByIdentifier(identifier);
        api.addFilter({ identifier: identifier, value: newValue });
        api.setPage({
            from: 0,
            size: pageSize
        });
        api.search();
    }
};

type PromotedFacetProps = {
    facet: search.SkFacetSet;
    showLabelInitials?: boolean;
    facetLabelProps?: {
        labelMap?: Record<string, string>;
        showLabelInitials?: boolean;
    };
    autoApply?: boolean;
    onFiltersChanged?: () => void;
};

const getInitials = (label: string) =>
    label
        .split(' ')
        .map((word) => word[0]?.toUpperCase() || '')
        .join('');

export default function PromotedFacet({ facet, facetLabelProps, autoApply, onFiltersChanged }: PromotedFacetProps) {
    const api = useSearchkit();
    const value = useMemo(() => {
        return getFilter(api, facet.identifier);
    }, [api, facet.identifier]);

    useEffect(() => {
        doFilter(api, facet.identifier, value);
    }, [api, facet.identifier, value]);
    const { labelMap, showLabelInitials } = facetLabelProps || {};

    const options: ResponsiveButtonGroupOption[] = facet.entries.map((entry: search.SkFacetSetEntry, index) => {
        const id = entry.id.replace('type_', ''); // These ids have 'type_' prefix while others do not. We don't want to duplicate definitions.
        let label = entry.label;
        if (labelMap && labelMap[entry.label]) {
            label = labelMap[entry.label];
        }
        const labelVal = showLabelInitials ? getInitials(label) : label;
        const icon = TYPE_TO_ICON[id] ? <SvgIcon component={TYPE_TO_ICON[id]} /> : <span>{labelVal}</span>;

        return {
            id: entry.label,
            label: label,
            icon: icon
        };
    });

    return (
        <ResponsiveButtonGroup
            useMobileWithBreakpoints={['xs', 's']}
            options={options}
            selectedOption={value}
            onToggle={(newValue: string) => {
                doFilter(api, facet.identifier, newValue);
            }}
        />
    );
}
